/**
 * Catálogo base de ejercicios y plantillas de rutina.
 *
 * Vive en core y no en el fixture porque lo necesita cualquier cuenta nueva: sin catálogo no se
 * puede armar una rutina, y sin rutina la app no sirve para nada. La API lo siembra en la planilla
 * compartida la primera vez.
 *
 * Es un catálogo GLOBAL: "un leg press es un leg press para todos" (§2). Los ejercicios propios del
 * usuario van a su planilla personal.
 *
 * Sobre las plantillas: son puntos de partida editables, no prescripciones. §10 y el propósito de la
 * app son claros en que el usuario define su rutina; lo que se evita acá es la pantalla en blanco.
 */

import type { Ejercicio, Musculo, Patron, TipoEjercicio } from './types.ts';

const ej = (
  id: string,
  nombre: string,
  primario: Musculo,
  secundarios: Musculo[],
  patron: Patron,
  tipo: TipoEjercicio = 'maquina',
): Ejercicio => ({
  id,
  nombre,
  musculo_primario: primario,
  musculos_secundarios: secundarios,
  tipo,
  patron,
  unilateral: false,
  es_global: true,
  creado_por_user_id: null,
  // 10–12 es el rango de trabajo más común en máquinas. El usuario lo cambia por ejercicio.
  rango_reps: [10, 12],
});

export const CATALOGO_BASE: Ejercicio[] = [
  // --- Pecho ---
  ej('ej-chest-press', 'Chest press', 'pecho', ['triceps', 'hombro_anterior'], 'empuje'),
  ej('ej-press-banca', 'Press de banca', 'pecho', ['triceps', 'hombro_anterior'], 'empuje', 'barra'),
  ej('ej-press-inclinado', 'Press inclinado', 'pecho', ['hombro_anterior', 'triceps'], 'empuje', 'mancuerna'),
  ej('ej-aperturas', 'Aperturas (pec deck)', 'pecho', ['hombro_anterior'], 'empuje'),
  ej('ej-fondos', 'Fondos', 'pecho', ['triceps', 'hombro_anterior'], 'empuje', 'peso_corporal'),

  // --- Espalda ---
  ej('ej-row', 'Row', 'dorsales', ['biceps', 'espalda_alta'], 'tiron'),
  ej('ej-pull-down', 'Pull down', 'dorsales', ['biceps', 'espalda_alta'], 'tiron'),
  ej('ej-remo-barra', 'Remo con barra', 'dorsales', ['biceps', 'espalda_alta', 'lumbar'], 'tiron', 'barra'),
  ej('ej-dominadas', 'Dominadas', 'dorsales', ['biceps', 'espalda_alta'], 'tiron', 'peso_corporal'),
  ej('ej-face-pull', 'Face pull', 'hombro_posterior', ['trapecio', 'espalda_alta'], 'tiron'),
  ej('ej-back-extension', 'Back extension', 'lumbar', ['gluteos', 'isquios'], 'core'),

  // --- Hombros ---
  ej('ej-shoulder-press', 'Shoulder press', 'hombro_anterior', ['triceps', 'hombro_lateral'], 'empuje'),
  ej('ej-elevaciones-laterales', 'Elevaciones laterales', 'hombro_lateral', ['trapecio'], 'empuje', 'mancuerna'),
  ej('ej-encogimientos', 'Encogimientos', 'trapecio', [], 'tiron', 'mancuerna'),

  // --- Brazos ---
  ej('ej-triceps-press', 'Triceps press', 'triceps', [], 'empuje'),
  ej('ej-triceps-polea', 'Tríceps en polea', 'triceps', [], 'empuje'),
  ej('ej-biceps-curl', 'Biceps curl', 'biceps', ['antebrazo'], 'tiron'),
  ej('ej-curl-martillo', 'Curl martillo', 'biceps', ['antebrazo'], 'tiron', 'mancuerna'),

  // --- Pierna ---
  ej('ej-leg-press', 'Leg press', 'cuadriceps', ['gluteos', 'isquios'], 'pierna'),
  ej('ej-leg-extension', 'Leg extension', 'cuadriceps', [], 'pierna'),
  ej('ej-leg-curl', 'Leg curl', 'isquios', ['gemelos'], 'pierna'),
  ej('ej-sentadilla', 'Sentadilla', 'cuadriceps', ['gluteos', 'isquios', 'lumbar'], 'pierna', 'barra'),
  ej('ej-peso-muerto', 'Peso muerto', 'isquios', ['gluteos', 'lumbar', 'dorsales'], 'pierna', 'barra'),
  ej('ej-hip-thrust', 'Hip thrust', 'gluteos', ['isquios'], 'pierna'),
  ej('ej-abductores', 'Abductores', 'gluteos', [], 'pierna'),
  ej('ej-aductores', 'Aductores', 'aductores', [], 'pierna'),
  ej('ej-calf-raise', 'Calf raise', 'gemelos', [], 'pierna'),

  // --- Core ---
  ej('ej-abdominal', 'Abdominal', 'core', ['oblicuos'], 'core'),
  ej('ej-plancha', 'Plancha', 'core', ['oblicuos', 'lumbar'], 'core', 'peso_corporal'),
  ej('ej-rueda-abdominal', 'Rueda abdominal', 'core', ['oblicuos'], 'core'),
];

export const CATALOGO_POR_ID = new Map(CATALOGO_BASE.map((e) => [e.id, e]));

// ---------------------------------------------------------------------------
// Plantillas de rutina
// ---------------------------------------------------------------------------

export interface DiaDePlantilla {
  nombre: string;
  ejercicios: string[];
}

export interface PlantillaRutina {
  id: string;
  nombre: string;
  /** Para qué sirve, en una línea. Se muestra al elegir. */
  descripcion: string;
  dias: DiaDePlantilla[];
}

/**
 * Puntos de partida, no prescripciones. Al elegir una, el usuario ve los ejercicios y puede
 * quitar, agregar y reordenar antes de guardar.
 */
export const PLANTILLAS: PlantillaRutina[] = [
  {
    id: 'rotacion-3',
    nombre: 'Rotación de 3 días',
    descripcion: 'Torso empuje, torso tirón y pierna. El split más común para 3 días por semana.',
    dias: [
      {
        nombre: 'Día 1 — pecho, espalda y brazos',
        ejercicios: ['ej-chest-press', 'ej-row', 'ej-triceps-press', 'ej-biceps-curl'],
      },
      {
        nombre: 'Día 2 — hombros, espalda alta y core',
        ejercicios: [
          'ej-shoulder-press',
          'ej-pull-down',
          'ej-elevaciones-laterales',
          'ej-back-extension',
          'ej-abdominal',
        ],
      },
      {
        nombre: 'Día 3 — piernas',
        ejercicios: ['ej-leg-press', 'ej-leg-curl', 'ej-leg-extension', 'ej-calf-raise'],
      },
    ],
  },
  {
    id: 'empuje-tiron-pierna',
    nombre: 'Empuje / Tirón / Pierna',
    descripcion: 'Separa por patrón de movimiento. Deja cada grupo con más descanso entre sesiones.',
    dias: [
      {
        nombre: 'Empuje — pecho, hombro y tríceps',
        ejercicios: ['ej-chest-press', 'ej-shoulder-press', 'ej-aperturas', 'ej-triceps-polea'],
      },
      {
        nombre: 'Tirón — espalda y bíceps',
        ejercicios: ['ej-pull-down', 'ej-row', 'ej-face-pull', 'ej-biceps-curl'],
      },
      {
        nombre: 'Pierna',
        ejercicios: ['ej-leg-press', 'ej-leg-curl', 'ej-hip-thrust', 'ej-calf-raise'],
      },
    ],
  },
  {
    id: 'cuerpo-completo',
    nombre: 'Cuerpo completo',
    descripcion: 'Un solo día que toca todo. Sirve para 2 o 3 veces por semana sin rotación.',
    dias: [
      {
        nombre: 'Cuerpo completo',
        ejercicios: [
          'ej-chest-press',
          'ej-pull-down',
          'ej-leg-press',
          'ej-shoulder-press',
          'ej-leg-curl',
          'ej-abdominal',
        ],
      },
    ],
  },
  {
    id: 'cuatro-dias',
    nombre: 'Cuatro días',
    descripcion: 'Torso y pierna dos veces cada uno. Para quien va cuatro veces por semana.',
    dias: [
      { nombre: 'Torso A — empuje', ejercicios: ['ej-chest-press', 'ej-shoulder-press', 'ej-triceps-press'] },
      { nombre: 'Pierna A', ejercicios: ['ej-leg-press', 'ej-leg-curl', 'ej-calf-raise'] },
      { nombre: 'Torso B — tirón', ejercicios: ['ej-pull-down', 'ej-row', 'ej-biceps-curl', 'ej-face-pull'] },
      { nombre: 'Pierna B', ejercicios: ['ej-hip-thrust', 'ej-leg-extension', 'ej-abductores'] },
    ],
  },
];

export function plantillaPorId(id: string): PlantillaRutina | undefined {
  return PLANTILLAS.find((p) => p.id === id);
}

/** Los ejercicios de una plantilla, resueltos contra el catálogo. */
export function ejerciciosDePlantilla(p: PlantillaRutina): { nombre: string; ejercicios: Ejercicio[] }[] {
  return p.dias.map((d) => ({
    nombre: d.nombre,
    ejercicios: d.ejercicios
      .map((id) => CATALOGO_POR_ID.get(id))
      .filter((e): e is Ejercicio => e !== undefined),
  }));
}
